import { useTranslation } from "react-i18next";
import SectionHeader from "../../../util/component/SectionHeader";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import Threshold from "./treshold/Threshold";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { IExpiredThresholdForm, IOFindExpiredConfigurationParams } from "../models/models";
import { EXPIRED_MANAGEMENT_CONFIGURATION_FORM_VALIDATION_SCHEMA, EXPIRED_PRACTICES_QUERY_KEY, EXPIRED_THRESHOLD_INITIAL_VALUE } from "../config";
import RecoveryCompanies from "./recovery-companies/RecoveryCompanies";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { findExpiredConfiguration, updateExpiredConfiguration } from "../services/http.services";
import CustomLoader from "../../../util/component/CustomLoader";
import { useContext, useEffect } from "react";
import { GlobalContext } from "../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../util/state/globalVariables";
import ContentBody from "../common/content-body/ContentBody";
import { setSnackbar } from "../../../util/state/globalAction";
import TenantForm from "../../booking/components/multitenant/TenantForm";


const ExpiredManagementConfiguration = () => {

    const {t} = useTranslation(['expiredManagement']);
    const theme = useTheme();
    const { globalData, dispatchGlobalData } = useContext(GlobalContext);

    const tenantForm = useForm({ defaultValues: { tenant: globalData?.user.legalEntities?.[0]?.name ?? "" } })
    const tenantWatch = useWatch({ control: tenantForm.control, name: "tenant" });

    const expiredManagementConfigurationForm = useForm<IExpiredThresholdForm>({
        defaultValues: EXPIRED_THRESHOLD_INITIAL_VALUE,
        resolver: yupResolver(EXPIRED_MANAGEMENT_CONFIGURATION_FORM_VALIDATION_SCHEMA)
    });

    const onReset = () => {
        expiredManagementConfigurationForm.reset();
    }

    const onConfirm = (data: IExpiredThresholdForm) => {

        const req: IOFindExpiredConfigurationParams = {
            tenant: data.tenant,
            tolleranceDaysST: data.tolleranceThreshold.shortTerm,
            firstSmsDaysST: data.firstSMSThreshold.shortTerm,
            recoveryCompanyDaysST: data.recoveryCompanyThreshold.shortTerm,
            formalNoticeDaysST: data.warningLetterThreshold.shortTerm,
            lawSuitDaysST: data.complaintThreshold.shortTerm,
            tolleranceDaysSub: data.tolleranceThreshold.subscription,
            firstSmsDaysSub: data.firstSMSThreshold.subscription,
            recoveryCompanyDaysSub: data.recoveryCompanyThreshold.subscription,
            formalNoticeDaysSub: data.warningLetterThreshold.subscription,
            lawSuitDaysSub: data.complaintThreshold.subscription,
            tolleranceDaysCor: data.tolleranceThreshold.corporate,
            firstSmsDaysCor: data.firstSMSThreshold.corporate,
            recoveryCompanyDaysCor: data.recoveryCompanyThreshold.corporate,
            formalNoticeDaysCor: data.warningLetterThreshold.corporate,
            lawSuitDaysCor: data.complaintThreshold.corporate,
            recoveryCompanies: data.recoveryCompanies.map(rc => (
                {
                    name: rc.name,
                    email: rc.email,
                    tenant: rc.tenant,
                    amount: !!rc.amount ? Math.round(Number(rc.amount)*100) : null
                }
            )) 
        }
        updateExpiredConfigurationQuery.mutate(req);

    }

    const viewSnackbarMessage = (message: string, type = snackbarTypes.error) => {
        dispatchGlobalData({
            type: setSnackbar,
            payload: {
                snackbar: {
                    message: t(message),
                    type: type,
                    show: true,
                    autoClose: true,
                }
            }
        });
    }

    const mappingForm = (data: IOFindExpiredConfigurationParams) : IExpiredThresholdForm => {
        return {
            tenant: data?.tenant || '',
            tolleranceThreshold: {
                shortTerm: data?.tolleranceDaysST || 0,
                subscription: data?.tolleranceDaysSub || 0,
                corporate: data?.tolleranceDaysCor || 0
            },
            firstSMSThreshold: {
                shortTerm: data?.firstSmsDaysST || 0,
                subscription: data?.firstSmsDaysSub || 0,
                corporate: data?.firstSmsDaysCor || 0
            },
            recoveryCompanyThreshold: {
                shortTerm: data?.recoveryCompanyDaysST || 0,
                subscription: data?.recoveryCompanyDaysSub || 0,
                corporate: data?.recoveryCompanyDaysCor || 0
            },
            warningLetterThreshold: {
                shortTerm: data?.formalNoticeDaysST || 0,
                subscription: data?.formalNoticeDaysSub || 0,
                corporate: data?.formalNoticeDaysCor || 0
            },
            complaintThreshold: {
                shortTerm: data?.lawSuitDaysST || 0,
                subscription: data?.lawSuitDaysSub || 0,
                corporate: data?.lawSuitDaysCor || 0
            },
            recoveryCompanies:  data?.recoveryCompanies ? data.recoveryCompanies?.map(rc => ({
                name: rc.name || '',
                email: rc.email || '',
                tenant: rc.tenant || '',
                amount: rc.amount != null ? (rc.amount / 100).toFixed(2) : '0'
            }) || []) : []
        }
    }

    const findExpiredConfigurationQuery = useQuery(
        [EXPIRED_PRACTICES_QUERY_KEY.FIND_EXPIRED_CONFIGURATION, { tenant: tenantWatch }],
        () => findExpiredConfiguration({ tenant: tenantWatch }),
        {
            onSuccess(data: any) {

                const resp: IOFindExpiredConfigurationParams = data.data;
                expiredManagementConfigurationForm.reset(mappingForm(resp));
                
            },
            onError() {
                viewSnackbarMessage('translation:error.tryLater');
            }
        }
    );

    const updateExpiredConfigurationQuery = useMutation(
        {
            mutationKey: [EXPIRED_PRACTICES_QUERY_KEY.UPDATE_EXPIRED_CONFIGURATION],
            mutationFn: (params: IOFindExpiredConfigurationParams) => {
                return updateExpiredConfiguration(params);
            },
            onSuccess(data: any) {

                viewSnackbarMessage('common.message.success', snackbarTypes.success);
                const resp: IOFindExpiredConfigurationParams = data.data;
                expiredManagementConfigurationForm.reset(mappingForm(resp));
            },
            onError() {
                viewSnackbarMessage('translation:error.tryLater');
            }
        }
    )

    return (
        <FormProvider {...expiredManagementConfigurationForm}>
            <SectionHeader title={t('expiredConfiguration.title')}/>

            <ContentBody>
                <Paper elevation={1} sx={{p: theme.spacing(2)}}>
                    <Box pt={1} pb={1}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} >
                                <FormProvider {...tenantForm}>
                                    <TenantForm name="tenant" multiple={false} />
                                </FormProvider>
                            </Grid>
                        </Grid>
                    </Box>

                    <Threshold title={"tolleranceThreshold"}/>
                    <Threshold title={"firstSMSThreshold"}/>
                    <Threshold title={"recoveryCompanyThreshold"}/>
                    <Threshold title={"warningLetterThreshold"}/>
                    <Threshold title={"complaintThreshold"}/>
                    <RecoveryCompanies/>

                    <Stack 
                        direction="row" 
                        alignItems={"center"} 
                        justifyContent={"flex-end"} 
                        columnGap={1}
                        mt={theme.spacing(6)} 
                        mb={{xs: 1}}
                    >
                        <Button 
                            variant="outlined"
                            sx={{ color: theme.palette.accent.main }}
                            onClick={onReset}
                        >
                            {t('common.button.reset')}
                        </Button>
                        <Button
                            color="accent"
                            onClick={expiredManagementConfigurationForm.handleSubmit(onConfirm)}
                        >
                            {t('common.button.confirm')}
                        </Button>
                    </Stack>
                </Paper>
                <CustomLoader loading={findExpiredConfigurationQuery.isFetching || updateExpiredConfigurationQuery.isLoading} />
            </ContentBody>
        </FormProvider>
    )
}

export default ExpiredManagementConfiguration;